// eslint-disable-next-line no-unused-vars
import { h, Component, Fragment } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import localUtil from '../../lib/util';

/**
 * "Who was here" gallery for a convention that has already happened. The regular
 * participant list in view.js only renders while the convention is ongoing or still
 * upcoming, so a finished convention used to say nothing at all about its shooters.
 *
 * Names come from `convention.participants`; profile photos come from the included
 * user records in `conventionStore.participants` (get-convention.js only projects
 * image fields when a single convention is requested).
 */

const AVATAR_SIZE = 56;

// Solid colors with enough contrast for white text in both light and dark mode.
// Picked per user id so a person keeps the same circle between visits.
const INITIALS_COLORS = [
    '#0d6efd',
    '#6610f2',
    '#6f42c1',
    '#d63384',
    '#dc3545',
    '#b45309',
    '#198754',
    '#0f766e',
    '#0b7285',
    '#495057',
];

function nameSeed(participant) {
    const name = `${participant.firstname || ''}${participant.lastname || ''}${participant.email || ''}`;
    return name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
}

function colorFor(participant) {
    const id = Number(participant.id);
    const seed = Number.isFinite(id) && id > 0 ? id : nameSeed(participant);
    return INITIALS_COLORS[seed % INITIALS_COLORS.length];
}

/**
 * Two letters for the fallback circle. displayNameShort() stringifies a missing
 * lastname into a literal "undefined", so scrub that before using the result.
 */
function initials(participant) {
    const short = localUtil.displayNameShort(participant);
    if (typeof short === 'string') {
        const clean = short.replace(/undefined/g, '').trim();
        if (clean) {
            return clean.slice(0, 2).toUpperCase();
        }
    }
    const name = localUtil.displayName(participant, true) || '';
    return name.trim().slice(0, 2).toUpperCase() || '?';
}

@observer
class ConventionParticipantsDone extends Component {
    render() {
        const { isInThePast } = this.props;
        const { conventionStore, userStore } = this.props.stores;
        const { convention = {}, participants: participantUsers = [] } = conventionStore;
        const { user = {} } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        if (!isInThePast || convention.hideParticipants) {
            return null;
        }
        const participants = convention.participants || [];
        if (participants.length === 0) {
            return null;
        }

        // Copy before sorting - convention.participants is a shared observable.
        const sorted = [...participants].sort((a, b) => (
            localUtil.displayName(a, true).localeCompare(localUtil.displayName(b, true), 'nb')
        ));

        const userById = {};
        participantUsers.forEach(participantUser => {
            userById[participantUser.id] = participantUser;
        });

        return (<>
            <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between mb-1 mt-3'>
                <span>
                    <Text id='convention.participants'>Participants</Text>
                    <span class='badge bg-secondary ms-2'>{sorted.length}</span>
                </span>
            </div>
            <div class={`d-flex flex-row flex-wrap justify-content-center gap-2 mx-3 px-3 py-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                {sorted.map(participant => {
                    const fullName = localUtil.displayName(participant, true);
                    const participantUser = userById[participant.id];
                    const image = participantUser ? localUtil.getImage({ user: participantUser }) : null;
                    return (
                        <div
                            key={participant.id}
                            class='d-flex flex-column align-items-center'
                            style='width: 72px;'
                            title={fullName}
                        >
                            {image ? (
                                <img
                                    src={image}
                                    alt={fullName}
                                    class='rounded-circle bg-body-secondary'
                                    style={`width: ${AVATAR_SIZE}px; height: ${AVATAR_SIZE}px; object-fit: cover;`}
                                />
                            ) : (
                                <div
                                    class='rounded-circle d-flex align-items-center justify-content-center text-white'
                                    style={`width: ${AVATAR_SIZE}px; height: ${AVATAR_SIZE}px; background-color: ${colorFor(participant)}; font-size: 1.1rem;`}
                                >
                                    {initials(participant)}
                                </div>
                            )}
                            <small
                                class='d-block text-body-secondary text-center text-truncate w-100 mt-1'
                                style='font-size: 0.75rem;'
                            >
                                {participant.firstname || fullName}
                            </small>
                        </div>
                    );
                })}
            </div>
        </>);
    }
}

export default ConventionParticipantsDone;
